/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import {
  DataGridPro,
  GridCallbackDetails,
  GridCellModesModel,
  GridColDef,
  GridEditMode,
  GridEventListener,
  GridPinnedColumns,
  GridRowIdGetter,
  GridRowModesModel,
  GridSlotsComponent,
} from "@mui/x-data-grid-pro";
import { SxProps } from "@mui/material";
import { GridSlotsComponentsProps, UncapitalizeObjectKeys } from "@mui/x-data-grid/internals";
import { menuProps } from "../../helpers";


interface ICustomEditDataGridProps {
  sx?: SxProps;
  columns: GridColDef<any>[];
  rows: any[];
  loading?: boolean | undefined;
  autoHeight?: boolean | undefined;
  getRowId?: GridRowIdGetter<any> | undefined;
  slots?: UncapitalizeObjectKeys<Partial<GridSlotsComponent>> | undefined;
  slotProps?: GridSlotsComponentsProps | undefined;
  disableColumnMenu?: boolean | undefined;
  hideFooter?: boolean | undefined;
  pinnedColumns?: GridPinnedColumns | undefined;
  rowModesModel?: GridRowModesModel | undefined;
  editMode?: GridEditMode | undefined;
  onCellEditStop?: GridEventListener<"cellEditStop"> | undefined;
  onCellEditStart?: GridEventListener<"cellEditStart"> | undefined;
  onRowModesModelChange?: ((rowModesModel: GridRowModesModel, details: GridCallbackDetails<any>) => void) | undefined;
  onRowEditStop?: GridEventListener<"rowEditStop"> | undefined;
  processRowUpdate?: ((newRow: any, oldRow: any) => any) | undefined;
  onProcessRowUpdateError?: ((error: any) => void) | undefined;
  cellModesModel?: GridCellModesModel | undefined;
  onCellModesModelChange?: ((cellModesModel: GridCellModesModel, details: GridCallbackDetails<any>) => void) | undefined;
  onCellClick?: GridEventListener<"cellClick"> | undefined;
}

const CustomEditDataGrid: React.FC<ICustomEditDataGridProps> = ({
  sx,
  rows,
  columns,
  loading,
  getRowId,
  slots,
  slotProps,
  autoHeight,
  disableColumnMenu,
  hideFooter = false,
  pinnedColumns,
  rowModesModel,
  editMode,
  onCellEditStop,
  onCellEditStart,
  onRowModesModelChange,
  onRowEditStop,
  processRowUpdate,
  onProcessRowUpdateError,
  cellModesModel,
  onCellModesModelChange,
  onCellClick,
}) => {

  const dataGridSxProps: SxProps = {
    /* ── Outer shell: matches AMS "overflow-hidden rounded-lg border bg-card" ── */
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    overflow: "hidden",
    backgroundColor: "var(--card)",
    color: "var(--foreground)",
    fontSize: "var(--font-size-sm)",

    "& .MuiDataGrid-overlayWrapper": { minHeight: 55 },
    "& .MuiDataGrid-overlay": { backgroundColor: "var(--card)", color: "var(--muted-foreground)" },

    "& .MuiDataGrid-row": {
      borderBottom: "1px solid var(--border)",
      fontSize: "var(--font-size-sm)",
      transition: "background-color 150ms cubic-bezier(0.4,0,0.2,1)",
      "&:hover": { backgroundColor: "var(--muted)" },
      "&:last-child": { borderBottom: "none" },
    },

    "& .MuiDataGrid-cell": {
      borderBottom: "none",
      color: "var(--foreground)",
      fontSize: "var(--font-size-sm)",
      padding: "0 16px",
    },

    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: "var(--font-weight-medium, 500)",
      fontSize: "var(--font-size-sm)",
      color: "var(--muted-foreground)",
    },
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "1px solid var(--border)",
      backgroundColor: "var(--card)",
      color: "var(--muted-foreground)",
      fontSize: "var(--font-size-sm)",
      minHeight: "48px !important",
      maxHeight: "48px !important",
    },
    "& .MuiDataGrid-columnHeader:hover": { backgroundColor: "transparent" },
    "& .MuiDataGrid-columnSeparator": { display: "none" },

    "& .MuiDataGrid-footerContainer": {
      borderTop: "1px solid var(--border)",
      backgroundColor: "var(--card)",
      color: "var(--muted-foreground)",
      minHeight: "48px",
    },

    "& .MuiTablePagination-root": { color: "var(--muted-foreground)", fontSize: "var(--font-size-sm)" },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "var(--font-size-sm)", color: "var(--muted-foreground)" },
    "& .MuiTablePagination-select": { fontSize: "var(--font-size-sm)", color: "var(--foreground)" },
    "& .MuiTablePagination-selectIcon": { color: "var(--muted-foreground)" },
    "& .MuiIconButton-root": {
      color: "var(--muted-foreground)",
      "&:hover": { backgroundColor: "var(--accent)" },
      "&.Mui-disabled": { color: "var(--muted-foreground)", opacity: 0.4 },
    },

    "& .MuiDataGrid-sortIcon": { color: "var(--muted-foreground)" },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: "var(--card)" },
  }
  return (
    <>
      <DataGridPro
        sx={{ ...dataGridSxProps, ...sx }}
        rows={rows}
        editMode={editMode}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        autoHeight={autoHeight}
        disableColumnMenu={disableColumnMenu}
        slots={slots}
        slotProps={{
          ...slotProps,
          baseSelect: { MenuProps: menuProps }
        }}
        initialState={{
          pinnedColumns,
        }}
        hideFooter={hideFooter}
        rowModesModel={rowModesModel}
        onCellEditStop={onCellEditStop}
        onRowModesModelChange={onRowModesModelChange}
        onRowEditStop={onRowEditStop}
        processRowUpdate={processRowUpdate}
        onCellEditStart={onCellEditStart}
        onProcessRowUpdateError={onProcessRowUpdateError}
        cellModesModel={cellModesModel}
        onCellModesModelChange={onCellModesModelChange}
        onCellClick={onCellClick}

      />
    </>
  );
};

export default CustomEditDataGrid;
